import type { StaffSession } from './types';

/**
 * HTTP client.
 *
 * Narrow on purpose, the same way the customer app's is: build the URL, attach
 * the bearer token, enforce a timeout, refresh the session once on a 401, and
 * turn every failure into an ApiError. Nothing above this line knows about
 * fetch, headers or status codes.
 *
 * The error contract is http/errors.ts: `{ message, code?, errors? }`, with the
 * status carrying the meaning. 422 is field validation, 409 is a state
 * conflict, 403 is a role refusal, 404 is also what you get for someone else's
 * row. Screens branch on `code` where the server names one and on `status`
 * otherwise; they never parse the message.
 */

export interface FieldError {
  field: string;
  message: string;
}

export class ApiError extends Error {
  /** 0 when the request never reached the server — offline, DNS, TLS. */
  readonly status: number;
  readonly code?: string;
  readonly fieldErrors?: FieldError[];

  constructor(
    status: number,
    message: string,
    options: { code?: string; fieldErrors?: FieldError[]; cause?: unknown } = {},
  ) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'ApiError';
    this.status = status;
    if (options.code !== undefined) this.code = options.code;
    if (options.fieldErrors !== undefined) this.fieldErrors = options.fieldErrors;
  }

  /** The one case a screen must handle without a status: the network is gone. */
  static offline(message = 'No connection. Check the signal and try again.'): ApiError {
    return new ApiError(0, message, { code: 'offline' });
  }

  static timeout(): ApiError {
    return new ApiError(0, 'The request timed out.', { code: 'timeout' });
  }

  /** Field errors keyed by field, for a form that renders them under inputs. */
  fieldMap(): Record<string, string> {
    const map: Record<string, string> = {};
    for (const entry of this.fieldErrors ?? []) map[entry.field] = entry.message;
    return map;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** Copy for a failure a screen has no better words for. */
export function errorMessage(error: unknown): string {
  if (isApiError(error)) return error.message;
  if (error instanceof Error && error.message !== '') return error.message;
  return 'Something went wrong.';
}

/* ------------------------------------------------------------ environment */

function readEnv(key: string, fallback: string): string {
  const value: unknown = import.meta.env[key];
  return typeof value === 'string' && value !== '' ? value : fallback;
}

/** Relative by default: the dev proxy and a production reverse proxy both work. */
const BASE_URL = readEnv('VITE_API_URL', '/v1').replace(/\/+$/, '');

const TIMEOUT_MS = Number.parseInt(readEnv('VITE_API_TIMEOUT_MS', '20000'), 10);

export const appVersion = readEnv('VITE_APP_VERSION', '0.0.0');

/* ----------------------------------------------------------------- wiring */

type RefreshHandler = () => Promise<StaffSession | null>;
type UnauthorizedHandler = () => void;

let getAccessToken: () => string | null = () => null;
let refreshHandler: RefreshHandler | null = null;
let onUnauthorized: UnauthorizedHandler | null = null;
let inFlightRefresh: Promise<StaffSession | null> | null = null;

/**
 * Registered once by the AuthProvider.
 *
 * Inverted rather than imported so the client does not depend on the auth
 * module that depends on it — the same shape configureClient() has in the
 * customer app.
 */
export function configureClient(handlers: {
  getAccessToken?: () => string | null;
  refresh?: RefreshHandler;
  onUnauthorized?: UnauthorizedHandler;
}): void {
  if (handlers.getAccessToken) getAccessToken = handlers.getAccessToken;
  if (handlers.refresh) refreshHandler = handlers.refresh;
  if (handlers.onUnauthorized) onUnauthorized = handlers.onUnauthorized;
}

/**
 * At most one refresh in flight.
 *
 * A dashboard fires six queries at once; if the access token has just expired
 * all six get a 401 within the same tick. Six refreshes would rotate the
 * refresh token six times and — because rotation treats a reused token as
 * theft — sign the dispatcher out of a working session. One promise, shared.
 */
async function refreshOnce(): Promise<StaffSession | null> {
  if (!refreshHandler) return null;

  inFlightRefresh ??= refreshHandler()
    .catch(() => null)
    .finally(() => {
      inFlightRefresh = null;
    });

  return inFlightRefresh;
}

/* --------------------------------------------------------------- requests */

export type QueryValue = string | number | boolean | undefined | null;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /**
   * An array value is appended once per element — that is how the admin filter
   * sends `?status=a&status=b`, which the server accepts alongside `a,b`.
   */
  query?: Record<string, QueryValue | QueryValue[]>;
  /** False for the two endpoints that must not carry a token: login, refresh. */
  authenticated?: boolean;
  signal?: AbortSignal;
  timeoutMs?: number;
  headers?: Record<string, string>;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const suffix = path.startsWith('/') ? path : `/${path}`;
  // A relative base has no origin to give URL(), so the search string is built
  // by hand and the whole thing stays relative.
  const params = new URLSearchParams();

  if (query) {
    for (const [key, value] of Object.entries(query)) {
      const values = Array.isArray(value) ? value : [value];
      for (const entry of values) {
        // Blank is absent, matching the server's own reading of an empty param.
        if (entry === undefined || entry === null || entry === '') continue;
        params.append(key, String(entry));
      }
    }
  }

  const search = params.toString();
  return search === '' ? `${BASE_URL}${suffix}` : `${BASE_URL}${suffix}?${search}`;
}

interface ErrorPayload {
  message?: unknown;
  error?: unknown;
  code?: unknown;
  errors?: unknown;
}

function parseFieldErrors(payload: ErrorPayload): FieldError[] | undefined {
  if (!Array.isArray(payload.errors)) return undefined;
  const parsed: FieldError[] = [];
  for (const entry of payload.errors) {
    if (typeof entry !== 'object' || entry === null) continue;
    const candidate = entry as { field?: unknown; message?: unknown };
    if (typeof candidate.field !== 'string') continue;
    parsed.push({
      field: candidate.field,
      message: typeof candidate.message === 'string' ? candidate.message : '',
    });
  }
  return parsed.length > 0 ? parsed : undefined;
}

async function readError(response: Response): Promise<ApiError> {
  let payload: ErrorPayload = {};
  try {
    payload = (await response.json()) as ErrorPayload;
  } catch {
    // An HTML error page or an empty 502. The status alone carries it.
  }

  const message =
    typeof payload.message === 'string'
      ? payload.message
      : typeof payload.error === 'string'
        ? payload.error
        : `Request failed (${response.status})`;

  const options: { code?: string; fieldErrors?: FieldError[] } = {};
  if (typeof payload.code === 'string') options.code = payload.code;
  const fieldErrors = parseFieldErrors(payload);
  if (fieldErrors) options.fieldErrors = fieldErrors;

  return new ApiError(response.status, message, options);
}

async function execute<T>(path: string, options: RequestOptions, isRetry: boolean): Promise<T> {
  const {
    method = 'GET',
    body,
    query,
    authenticated = true,
    signal,
    timeoutMs = TIMEOUT_MS,
    headers = {},
  } = options;

  const controller = new AbortController();
  const timeout = window.setTimeout(() => {
    controller.abort();
  }, timeoutMs);
  const onExternalAbort = (): void => {
    controller.abort();
  };
  signal?.addEventListener('abort', onExternalAbort);

  try {
    const requestHeaders: Record<string, string> = {
      Accept: 'application/json',
      'X-Client-Platform': 'back-office',
      'X-Client-Version': appVersion,
      ...headers,
    };

    if (body !== undefined) requestHeaders['Content-Type'] = 'application/json';

    if (authenticated) {
      const token = getAccessToken();
      if (token !== null) requestHeaders.Authorization = `Bearer ${token}`;
    }

    const init: RequestInit = {
      method,
      headers: requestHeaders,
      signal: controller.signal,
    };
    // Assigned rather than spread: under exactOptionalPropertyTypes an explicit
    // `body: undefined` is not the same as no body at all.
    if (body !== undefined) init.body = JSON.stringify(body);

    const response = await fetch(buildUrl(path, query), init);

    /*
     * Refresh once, then give up.
     *
     * A second 401 after a successful refresh is not an expiry — it is a
     * customer token on a staff route, or a deactivated account, or a role the
     * route refuses. Retrying that forever is a loop, so the session is dropped
     * and the shell sends the user to /login.
     */
    if (response.status === 401 && authenticated && !isRetry) {
      const refreshed = await refreshOnce();
      if (refreshed !== null) return execute<T>(path, options, true);

      onUnauthorized?.();
      throw new ApiError(401, 'Your session has expired. Sign in again.', {
        code: 'sessionExpired',
      });
    }

    if (!response.ok) throw await readError(response);

    // 204 on logout, and on nothing else in this API.
    if (response.status === 204) return undefined as T;

    const text = await response.text();
    return (text === '' ? undefined : JSON.parse(text)) as T;
  } catch (error) {
    if (isApiError(error)) throw error;

    if (error instanceof DOMException && error.name === 'AbortError') {
      // Our own deadline or the caller's cancellation — TanStack Query aborts on
      // unmount, and reporting that as a timeout would flash a false error.
      throw signal?.aborted === true
        ? new ApiError(0, 'Request cancelled', { code: 'cancelled' })
        : ApiError.timeout();
    }
    if (error instanceof TypeError) {
      // fetch rejects with TypeError for DNS, TLS and connection failures.
      throw ApiError.offline();
    }
    throw new ApiError(0, 'Something went wrong.', { code: 'unknown', cause: error });
  } finally {
    window.clearTimeout(timeout);
    signal?.removeEventListener('abort', onExternalAbort);
  }
}

export const api = {
  request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return execute<T>(path, options, false);
  },
  get<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return execute<T>(path, { ...options, method: 'GET' }, false);
  },
  post<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method'> = {}): Promise<T> {
    return execute<T>(path, { ...options, method: 'POST', body }, false);
  },
  patch<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method'> = {}): Promise<T> {
    return execute<T>(path, { ...options, method: 'PATCH', body }, false);
  },
  delete<T>(path: string, options: Omit<RequestOptions, 'method'> = {}): Promise<T> {
    return execute<T>(path, { ...options, method: 'DELETE' }, false);
  },
};
